import React from 'react';
import { formatValue } from './formatters';
import { useAnimatedNumber } from './AnimatedValue';
import './ReportsNative.css';

/**
 * Semicircular SVG arc gauge per operations-labor.html / revenue-forecast.html:
 * onyx-100 track, Lodge Orange value arc, mono center value + sublabel,
 * mono bounds labels, optional dashed target tick.
 *
 * Props:
 *   value        current value (same units as min/max)
 *   min, max     bounds (default 0 / 100)
 *   format       center-value format key or fn (default 'percent1')
 *   boundsFormat bounds format (default 'percent', 0 decimals)
 *   valueText    override the formatted center value
 *   sublabel     small line under the value ('of total hours worked')
 *   target       optional target value — draws a dashed tick
 *   targetLabel  text near the tick ('TARGET ≤5%')
 *   color        value arc color (default var(--lodge-orange-400))
 *   width        rendered width px (default 300; keeps mock 220×142 ratio)
 *   strokeWidth  arc thickness in viewBox units (default 15)
 *   delta        { tone: 'success'|'error'|'warning'|'neutral', label } chip under the gauge
 *   meta         mono sub text next to the delta chip ('267.1 OT hrs · 19,076.9 total')
 */
export function ArcGauge({
  value,
  min = 0,
  max = 100,
  format = 'percent1',
  boundsFormat = 'percent',
  valueText,
  sublabel,
  target,
  targetLabel,
  color = 'var(--lodge-orange-400)',
  width = 300,
  strokeWidth = 15,
  delta,
  meta,
  className,
  ariaLabel,
}) {
  // Fixed viewBox geometry (matches revenue-forecast.html gauge)
  const VB_W = 220;
  const VB_H = 142;
  const cx = 110;
  const cy = 115;
  const R = 85;
  const arcLen = Math.PI * R;

  const span = max - min || 1;
  // arc geometry follows the tweened value so data swaps sweep instead of snap
  const animatedValue = useAnimatedNumber(Number(value) || 0);
  const frac = Math.min(1, Math.max(0, ((Number(animatedValue) || 0) - min) / span));
  const dash = frac * arcLen;
  const arcPath = `M ${cx - R} ${cy} A ${R} ${R} 0 0 1 ${cx + R} ${cy}`;

  // Target tick geometry: angle sweeps 180° (min, left) → 0° (max, right)
  let tick = null;
  if (target !== undefined && target !== null) {
    const tFrac = Math.min(1, Math.max(0, (Number(target) - min) / span));
    const theta = Math.PI - Math.PI * tFrac;
    const cos = Math.cos(theta);
    const sin = Math.sin(theta);
    const r1 = R - strokeWidth;
    const r2 = R + strokeWidth + 1;
    tick = {
      x1: cx + r1 * cos,
      y1: cy - r1 * sin,
      x2: cx + r2 * cos,
      y2: cy - r2 * sin,
      labelX: cx + (r2 + 4) * cos,
      labelY: cy - (r2 + 4) * sin - 6,
      anchor: tFrac < 0.35 ? 'start' : tFrac > 0.65 ? 'end' : 'middle',
    };
  }

  const centerText = valueText ?? formatValue(animatedValue, format);

  return (
    <div className={className}>
      <div className="rn-gauge-wrap">
        <svg
          viewBox={`0 0 ${VB_W} ${VB_H}`}
          style={{ width, maxWidth: '100%', display: 'block', height: 'auto' }}
          role="img"
          aria-label={ariaLabel || `Gauge showing ${centerText}`}
        >
          <path
            d={arcPath}
            fill="none"
            style={{ stroke: 'var(--lodge-onyx-100)' }}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
          {frac > 0 && (
            <path
              d={arcPath}
              fill="none"
              style={{ stroke: color }}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeDasharray={`${dash.toFixed(2)} ${(arcLen + 10).toFixed(2)}`}
            />
          )}
          {tick && (
            <>
              <line
                x1={tick.x1.toFixed(1)}
                y1={tick.y1.toFixed(1)}
                x2={tick.x2.toFixed(1)}
                y2={tick.y2.toFixed(1)}
                style={{ stroke: 'var(--lodge-onyx-300)' }}
                strokeWidth="1.5"
                strokeDasharray="3 3"
              />
              {targetLabel && (
                <text
                  x={tick.labelX.toFixed(1)}
                  y={tick.labelY.toFixed(1)}
                  textAnchor={tick.anchor}
                  style={{
                    fontFamily: 'var(--font-sans)',
                    fontSize: 8.5,
                    fontWeight: 600,
                    fill: 'var(--lodge-onyx-400)',
                    letterSpacing: '.04em',
                  }}
                >
                  {targetLabel}
                </text>
              )}
            </>
          )}
          <text
            x={cx}
            y={102}
            textAnchor="middle"
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 26, // matches .rn-kpi-value — gauges read at the same scale as stat tiles
              fontWeight: 600,
              fill: 'var(--lodge-onyx-900)',
              letterSpacing: '-.02em',
            }}
          >
            {centerText}
          </text>
          {sublabel && (
            <text
              x={cx}
              y={120}
              textAnchor="middle"
              style={{ fontFamily: 'var(--font-sans)', fontSize: 10.5, fill: 'var(--lodge-onyx-400)' }}
            >
              {sublabel}
            </text>
          )}
          <text
            x={cx - R}
            y={136}
            textAnchor="middle"
            style={{ fontFamily: 'var(--font-mono)', fontSize: 9.5, fill: 'var(--lodge-onyx-300)' }}
          >
            {formatValue(min, boundsFormat)}
          </text>
          <text
            x={cx + R}
            y={136}
            textAnchor="middle"
            style={{ fontFamily: 'var(--font-mono)', fontSize: 9.5, fill: 'var(--lodge-onyx-300)' }}
          >
            {formatValue(max, boundsFormat)}
          </text>
        </svg>
      </div>
      {(delta || meta) && (
        <div className="rn-gauge-meta">
          {delta && <span className={`rn-delta rn-delta-${delta.tone || 'neutral'}`}>{delta.label}</span>}
          {meta && <span className="rn-gauge-sub">{meta}</span>}
        </div>
      )}
    </div>
  );
}

export default ArcGauge;
